import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { MedplumClient } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { ProfileContext } from "@/contexts/profile";
import { fetchUploadedFiles } from "@/file-uploads/services/file-uploads";
import { useAppDispatch } from "@/store";
import { loadUploadedFiles } from "@/store/slices/uploaded-files";

function useFiles(medplum: MedplumClient, clientId: string) {
  const dispatch = useAppDispatch();
  const [selectedFile, setSelectedFile] = useState<DocumentReference | undefined>(undefined);
  const { isPatient } = useContext(ProfileContext);

  useEffect(() => {
    if (!clientId) return;

    (async () => {
      try {
        const [uploadedFiles] = await fetchUploadedFiles(
          medplum,
          clientId,
          1, // First page
          100, // Load more files for the encounter view
          { fileTypes: [] }, // No filters
          isPatient,
        );
        dispatch(loadUploadedFiles(uploadedFiles));
      } catch (error) {
        console.error("Error loading files:", error);
      }
    })();
  }, [dispatch, medplum, clientId, isPatient]);

  return { selectedFile, setSelectedFile };
}

type FileContextType = ReturnType<typeof useFiles>;

const FileContext = createContext<FileContextType | null>(null);

export function FileProvider({
  medplum,
  clientId,
  children,
}: {
  medplum: MedplumClient;
  clientId: string;
  children: ReactNode;
}) {
  const files = useFiles(medplum, clientId);
  return <FileContext.Provider value={files}>{children}</FileContext.Provider>;
}

export function useFileContext() {
  const ctx = useContext(FileContext);
  if (!ctx) throw new Error("useFileContext must be used within FileProvider");
  return ctx;
}
